<script>
import Loading from "@/components/Loading.vue";
import Modal from "@/components/Modal.vue";
import { getAllOrg } from "@/services/orgServices";
import { getAllClinics } from "@/services/clinicServices";
import { editUser, getCurrentUser } from "@/services/userServices";
import { useAuthStore } from '@/stores/authStore'
import { getStateHealthOrgs } from "@/services/stateHealthOrgServices";

export default{
  components: {
    Loading,
    Modal,
  },

  created(){
    this.user_id = this.$route.params.id;
    this.get_data(this.user_id);
  },

  watch: {
    selectedClinic(newClinic) {
      if (newClinic && !this.user_data.locations.includes(newClinic)) {
        this.user_data.locations.push(newClinic);
        this.syncClinicName();
        this.selectedClinic = "";
      }
    },
  },

  computed: {
    canManageLocations() {
      return this.authStore.userRole === 'clinic_admin'
        || (this.authStore.userRole === 'admin' && this.user_role === 'clinic_admin');
    },

    availableClinics() {
      if (this.authStore.userRole !== 'admin') {
        return this.clinicsSelection;
      }

      if (!this.user_data.OrganizationUuid) {
        return [];
      }

      return this.clinicsSelection.filter(
        (clinic) => clinic.organizationUuid === this.user_data.OrganizationUuid
      );
    },
  },

  data() {
    return {
      authStore: useAuthStore(),
      user_id: '',
      raw_user_data: {},
      user_role: "",
      user_data: {
        full_name: "",
        profession: "",
        specialization: "",
        clinic_name: "",
        contact_number: "",
        locations: [],
        additional_info: {},
        OrganizationUuid: "",
      },
      selectedClinic: "",
      clinicsJson: {},
      clinicsSelection: [],
      isLoading: false,
      modal: {
        isSubmitting: false,
      },
      isEditing: false,
      organizations: [],
      stateHealthOrgs: [],
      selectedStateHealthOrgUuid: "",
      validationError: "",
    }
  },
  methods: {
    async get_data(uuid) {
      this.isLoading = true;
      const result = await getCurrentUser(uuid);

      if (this.authStore.userRole === 'clinic_admin') {
        await this.get_clinics();
      } else if (this.authStore.userRole === 'admin') {
        await Promise.all([
          this.get_org(),
          this.get_state_orgs(),
          this.get_clinics(),
        ]);
      }

      if (result.user_info) {
        this.raw_user_data = result.user_info;
        this.convert_data(this.raw_user_data);
      }
      this.isLoading = false;
    },

    async get_org() {
      const result = await getAllOrg();
      if (result.data) {
        this.organizations = result.data;
      }
    },

    async get_state_orgs() {
      const result = await getStateHealthOrgs();
      this.stateHealthOrgs = result.data || [];
    },

    async get_clinics(){
      const result = await getAllClinics();
      this.clinicsSelection = [];

      if(result.data){
        this.clinicsSelection = result.data.map((clinic) => {
          this.clinicsJson[clinic.LocationUuid] = clinic.ClinicName;
          return {
            name: clinic.ClinicName,
            uuid: clinic.LocationUuid,
            organizationUuid: clinic.OrganizationUuid,
          }
        });
      }
    },

    convert_data(input_data){
      this.user_role = input_data.Role || "";
      this.user_data = {
        full_name: input_data.FullName || "",
        profession: input_data.Profession || "",
        specialization: input_data.Specialization || "",
        clinic_name: input_data.ClinicName || "",
        contact_number: input_data.ContactNumber || "",
        locations: input_data.Locations ? Object.values(input_data.Locations) : [],
        additional_info: input_data.AdditionalInfo || {},
        OrganizationUuid: input_data.OrganizationUuid || "",
      };
      this.selectedStateHealthOrgUuid = this.user_data.additional_info?.StateHealthOrgUuid || "";
      this.syncClinicName();
      this.validationError = "";
    },

    remove_clinic(key){
      this.user_data.locations.splice(key, 1);
      this.syncClinicName();
    },

    syncClinicName() {
      const firstClinicUuid = this.user_data.locations[0];
      this.user_data.clinic_name = firstClinicUuid ? this.clinicsJson[firstClinicUuid] || this.user_data.clinic_name : "";
    },

    close_modal(){
      this.modal.isSubmitting = false;
      this.isEditing = false;
      this.convert_data(this.raw_user_data);
    },

    validateForm() {
      if (!this.user_data.full_name.trim()) {
        this.validationError = "Full name is required.";
        return false;
      }
      if (this.authStore.userRole === 'admin' && this.user_role === 'state_health_org' && !this.selectedStateHealthOrgUuid) {
        this.validationError = "Choose the state organization for this account.";
        return false;
      }
      if (this.authStore.userRole === 'admin' && this.user_role !== 'state_health_org' && !this.user_data.OrganizationUuid) {
        this.validationError = "Choose an organization for this account.";
        return false;
      }
      if (this.canManageLocations && this.user_data.locations.length === 0) {
        this.validationError = "Assign at least one location.";
        return false;
      }
      if (this.authStore.userRole === 'admin' && this.user_role === 'clinic_admin') {
        const hasLocationOutsideOrganization = this.user_data.locations.some((locationUuid) => {
          const clinic = this.clinicsSelection.find((item) => item.uuid === locationUuid);
          return clinic && clinic.organizationUuid !== this.user_data.OrganizationUuid;
        });

        if (hasLocationOutsideOrganization) {
          this.validationError = "Every assigned location must belong to the selected organization.";
          return false;
        }
      }
      this.validationError = "";
      return true;
    },

    openSubmitModal() {
      if (this.validateForm()) {
        this.modal.isSubmitting = true;
      }
    },

    async submit_edit(){
      this.isLoading = true;
      this.isEditing = false;
      this.modal.isSubmitting = false;

      if (this.user_role === 'state_health_org') {
        this.user_data.OrganizationUuid = "";
        this.user_data.locations = [];
        this.user_data.clinic_name = "";
        this.user_data.additional_info = {
          ...(this.user_data.additional_info || {}),
          StateHealthOrgUuid: this.selectedStateHealthOrgUuid,
        };
      } else {
        this.syncClinicName();
      }
      this.user_data.role = this.user_role;

      const result = await editUser(this.user_data, this.user_id);
      if(result){
        await this.get_data(this.user_id);
      }
      this.isLoading = false;
    },
  }
}
</script>

<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <Modal
    :isVisible="modal.isSubmitting"
    promptText="Save the account changes?"
    yesText="Save"
    noText="Cancel"
    @yes="submit_edit()"
    @no="close_modal()"
  />

  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Account Management</p>
        <h1 class="page-title">{{ user_data.full_name || 'Edit account' }}</h1>
        <p class="page-subtitle">Update profile details and workspace assignment without mixing in login credential setup.</p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/users')">
        <i class="fas fa-arrow-left"></i> Back to accounts
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">Profile</h2>
              <p class="section-description">Basic identity and optional professional information.</p>
            </div>
            <span class="badge-soft" :class="{ success: isEditing }">{{ isEditing ? 'Editing' : 'View mode' }}</span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Full name <span class="required">*</span></label>
              <input class="ui-input" :disabled="!isEditing" v-model="user_data.full_name">
            </div>
            <div class="form-section">
              <label class="field-label">Contact number</label>
              <input class="ui-input" :disabled="!isEditing" v-model="user_data.contact_number">
            </div>
            <div class="form-section">
              <label class="field-label">Profession</label>
              <input class="ui-input" :disabled="!isEditing" v-model="user_data.profession">
            </div>
            <div class="form-section">
              <label class="field-label">Specialization</label>
              <input class="ui-input" :disabled="!isEditing" v-model="user_data.specialization">
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Workspace assignment</h2>
              <p class="section-description">This controls what the account can manage after signing in.</p>
            </div>
            <span class="badge-soft">{{ user_role.replace('_', ' ') || 'Unassigned role' }}</span>
          </div>

          <div class="form-grid">
            <div class="form-section full-width" v-if="authStore.userRole === 'admin'">
              <label class="field-label">Account role <span class="required">*</span></label>
              <select class="ui-select" :disabled="!isEditing" v-model="user_role">
                <option value="clinic_admin">Clinic admin</option>
                <option value="state_health_org">State health org</option>
              </select>
              <span class="field-hint">Clinic admins manage one organization. State health org accounts are read-only and use State Access.</span>
            </div>

            <div class="form-section full-width" v-if="authStore.userRole === 'admin' && user_role === 'state_health_org'">
              <label class="field-label">State organization <span class="required">*</span></label>
              <select class="ui-select" :disabled="!isEditing" v-model="selectedStateHealthOrgUuid">
                <option value="">Select state organization</option>
                <option v-for="org in stateHealthOrgs" :key="org.StateHealthOrgUuid" :value="org.StateHealthOrgUuid">
                  {{ org.Name }} ({{ org.StateName }})
                </option>
              </select>
              <span class="field-hint">This account is read-only and sees clinics assigned in State Access.</span>
            </div>

            <div class="form-section full-width" v-else-if="authStore.userRole === 'admin'">
              <label class="field-label">Organization <span class="required">*</span></label>
              <select class="ui-select" :disabled="!isEditing" v-model="user_data.OrganizationUuid">
                <option value="">Select organization</option>
                <option v-for="org in organizations" :key="org.OrganizationUuid" :value="org.OrganizationUuid">
                  {{ org.OrganizationName }}
                </option>
              </select>
            </div>

            <div class="form-section full-width" v-if="canManageLocations">
              <label class="field-label">Assigned locations <span class="required">*</span></label>
              <div class="tag-list">
                <span v-if="user_data.locations.length === 0" class="table-secondary">No locations assigned.</span>
                <span class="tag-pill" v-for="(clinic_id, ind) in user_data.locations" :key="clinic_id">
                  {{ clinicsJson[clinic_id] || 'Unknown clinic' }}
                  <button type="button" v-if="isEditing" @click="remove_clinic(ind)">
                    <i class="fas fa-times"></i>
                  </button>
                </span>
              </div>
              <select class="ui-select mt-2" v-if="isEditing" v-model="selectedClinic">
                <option value="">Add location</option>
                <option v-for="clinic in availableClinics" :key="clinic.uuid" :value="clinic.uuid">
                  {{ clinic.name }}
                </option>
              </select>
              <span v-if="authStore.userRole === 'admin'" class="field-hint">
                Only locations in the selected organization can be assigned.
              </span>
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" v-if="!isEditing" @click="$router.push('/users')">Back</button>
          <button class="ui-button primary" v-if="!isEditing" @click="isEditing = true">
            <i class="fas fa-edit"></i> Edit account
          </button>
          <button class="ui-button secondary" v-if="isEditing" @click="close_modal()">Cancel</button>
          <button class="ui-button primary" v-if="isEditing" @click="openSubmitModal()">
            <i class="fas fa-save"></i> Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
